import React, { useContext, useEffect, useMemo, useState } from "react";
import {Grommet, ResponsiveContext, ThemeContext} from "grommet";
import {deepMerge} from "grommet/utils";
import { ThemeProvider as NextThemesProvider, useTheme } from "next-themes";
import theme from "../utils/theme";
import { useRouter } from 'next/router';
import { isPayerPage } from "../utils/payerPages";

const ThemeProvider = ({ children, initialTheme, userAgent }) => {
    return (
        <NextThemesProvider attribute="class" defaultTheme="light" enableSystem={true} themes={['light', 'dark']}>
            <GrommetThemeProvider initialTheme={initialTheme} userAgent={userAgent}>
                {children}
            </GrommetThemeProvider>
        </NextThemesProvider>
    );
};

const GrommetThemeProvider = ({ children, initialTheme, userAgent }) => {
    const { resolvedTheme } = useTheme();
    const { asPath } = useRouter();

    /*
        Persist the RESOLVED mode, and only ever 'light' or 'dark'. The server reads this
        cookie to pick its render mode and cannot evaluate 'system' itself, so storing the
        preference verbatim left it guessing light while the blocking script in _document.js
        resolved the same cookie to dark via matchMedia.

        The guard matters because resolvedTheme really can be the literal 'system' here: on
        the first client render next-themes seeds systemTheme from the same storage key as
        the preference, and only the matchMedia effect corrects it to light/dark. Writing
        that through would downgrade the stored value.
    */
    useEffect(() => {
        if (resolvedTheme === 'light' || resolvedTheme === 'dark') {
            // A sandboxed frame throws on this. The cookie only saves the next server render
            // from guessing the mode, so going without it is fine.
            try {
                document.cookie = `theme=${resolvedTheme};path=/;max-age=31536000;SameSite=Lax`;
            } catch {
                // Nothing to do.
            }
        }
    }, [resolvedTheme]);

    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    /*
        The server render and the browser's first render must agree. The server takes the
        mode from the initialTheme prop injected in _document.js, and the browser takes that
        same value back from __INITIAL_THEME__, which _document.js bakes into the page. The
        browser must not re-read the cookie: a page optimised at build time was rendered
        light whatever the cookie says.

        resolvedTheme deliberately does not participate until after mount. next-themes 0.2.1
        seeds its state synchronously inside the useState initialisers, so resolvedTheme is
        already truthy on the browser's first render and would silently override whatever the
        server chose. For an auto/system user it is also the literal 'system' at that point,
        which normalises to light and hands a dark-OS user light markup over a dark server
        render. Deferring to `mounted` keeps the two renders identical and lets next-themes
        correct the mode once, afterwards.
    */
    const serverTheme = initialTheme || (typeof window !== 'undefined' ? window.__INITIAL_THEME__ : undefined);
    const settledTheme = resolvedTheme === 'dark' || resolvedTheme === 'light' ? resolvedTheme : undefined;
    let mode = ((mounted && settledTheme) || serverTheme || 'light') === 'dark' ? 'dark' : 'light';

    /*
        Always light: signup and the payer pages. Every one of these is shown to someone
        who is not our user and has no theme preference with us, so they render the
        merchant's branding on a known background rather than inheriting a dark mode
        nobody here chose.
    */
    // asPath carries the query and hash (/signup?confirmation=true), so test the path alone
    const path = (asPath || '').split(/[?#]/)[0];
    mode = /^\/signup($|\/)/.test(path) || isPayerPage(path) ? 'light' : mode;

    /*
        Grommet decides its breakpoint from this, and falls back to tablet without it. That
        fallback is why a phone was sent a tablet layout and then moved every block into
        place once Grommet's resize effect measured the real width after mount: the balance
        summary dropped 240px, the amount due crossed the page. The server render now names
        the device, so the first paint is already the right shape.

        Taken from the prop on the server and from __USER_AGENT__, which _document.js bakes
        into this very page, in the browser. Not navigator: this HTML can reach a device that
        did not request it - a build-time page, or a /p/ render served from the edge cache -
        and the browser's first render has to reproduce the server's or React throws #425.
        The resize effect still corrects the layout from the real width straight after.
    */
    const serverUserAgent = userAgent || (typeof window !== 'undefined' ? window.__USER_AGENT__ : undefined);

    return (
        <Grommet themeMode={mode} theme={theme} full className="grommet-wrapper" userAgent={serverUserAgent || undefined}>
            <PhoneButtons>{children}</PhoneButtons>
        </Grommet>
    );
};

/*
    Medium buttons, primary and secondary alike, are 35px tall: under the 44px a thumb
    needs on a phone. Small and plain buttons keep their size, and so does desktop.

    Merged into the theme rather than through ThemeContext.Extend: Grommet attaches Extend
    after creating the context and declares no side effects, so the bundler drops it. The
    provider is always rendered, with only its value changing, because swapping the
    wrapper would remount the page when a phone turns to landscape and wipe any form.
*/
const PHONE_BUTTONS = {
    button: { size: { medium: { pad: { vertical: '10.5px' } } } },
    // Menus are at least 200px wide on a phone, so their items are easier to tap, and
    // still fill a drop wider than that
    menu: { container: { width: { min: 'max(200px, 100%)' } } },
};

export const PhoneButtons = ({ children }) => {
    const size = useContext(ResponsiveContext);
    const theme = useContext(ThemeContext);
    const phoneTheme = useMemo(() => deepMerge(theme, PHONE_BUTTONS), [theme]);
    return (
        <ThemeContext.Provider value={size === "small" ? phoneTheme : theme}>
            {children}
        </ThemeContext.Provider>
    );
};

export default ThemeProvider;
